import { useEffect, useState } from "react";
import { Alert, Button } from "flowbite-react";
import PhotoUpload from "./PhotoUpload";
import SurveySection from "./SurveySection";
import axios from "../../config/axiosInstance";
import { SURVEYS } from "../../config/apiConfig";
import { useQuery, useQueryClient } from "react-query";
import DrawerLoading from "../DrawerLoading";
import Compressor from "compressorjs";
import AutoCompleteInput from "../inputs/Autocomplete";
import CropModal from "./CropModal";

const SurveyPhoto = ({ setActiveStep, survey }) => {
  const queryClient = useQueryClient();
  const [mandatoryImages, setMandatoryImages] = useState([]);
  const [optionalImages, setOptionalImages] = useState([]);
  const [mandatoryTags, setMandatoryTags] = useState([]);
  const [optionalTags, setOptionalTags] = useState([]);
  const [listOptionalTags, setListOptionalTags] = useState([]);
  const [searchOptionalTag, setSearchOptionalTag] = useState();
  const [errorMandatoryImages, setErrorMandatoryImages] = useState(false);
  const [cropModalImage, setCropModalImage] = useState(null);
  const [pendingUpload, setPendingUpload] = useState(null);

  const { isLoading, data: tags } = useQuery(["tags"], async () => {
    const { data } = await axios.get(`${SURVEYS.GET_TAGS}`);
    setMandatoryTags(data?.data?.filter((tag) => tag.isMandatory));
    setOptionalTags(data?.data?.filter((tag) => !tag.isMandatory));
    setListOptionalTags(
      data?.data
        ?.filter((tag) => !tag.isMandatory)
        ?.map((tag) => ({ label: tag.name, value: tag.id }))
    );
    return data.data;
  });

  const { isLoading: isLoadingImages, data: images } = useQuery(
    ["images"],
    async () => {
      const { data } = await axios.get(
        `${SURVEYS.GET_SURVEY_IMAGES}/${survey.id}`
      );
      return data.data;
    }
  );

  const compressImage = (file, quality = 0.3) =>
    new Promise((resolve, reject) => {
      new Compressor(file, {
        quality,
        success: resolve,
        error: reject,
      });
    });

  const uploadImage = async (file, isMandatory, tag) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("surveyId", survey.id);
      formData.append("tag", JSON.stringify(tag));

      const response = await axios.post(SURVEYS.UPLOAD_IMAGE, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data.url;
    } catch (error) {
      console.error(`Erro ao enviar a imagem ${file.name}:`, error.message);
    }
  };

  const handleImageUpload = async (file, index, isMandatory, tag) => {
    if (!file) {
      if (isMandatory) {
        setMandatoryImages((prev) => {
          const updated = [...prev];
          updated[index] = null;
          return updated;
        });
      } else {
        setOptionalImages((prev) => prev.filter((_, i) => i !== index));
      }

      try {
        await axios.delete(SURVEYS.DELETE_IMAGE_SURVEY, { data: { tag } });
        console.log(`Imagem com tag ${tag.name} removida com sucesso.`);
      } catch (error) {
        console.error("Erro ao remover imagem no backend:", error);
      }

      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropModalImage(reader.result); // <- ESSENCIAL
      setPendingUpload({ file, index, isMandatory, tag });
    };
    reader.onerror = (error) => {
      console.error("Erro ao processar o arquivo:", error);
    };
    reader.readAsDataURL(file); // <- GARANTIR que isso roda
  };

  const handleCroppedImageUpload = async (
    croppedFile,
    { index, isMandatory, tag }
  ) => {
    try {
      const compressedFile = await compressImage(croppedFile);
      const url = await uploadImage(compressedFile, isMandatory, tag);

      const reader = new FileReader();
      reader.onload = () => {
        const imageData = { file: croppedFile, preview: reader.result, tag };

        if (isMandatory) {
          setMandatoryImages((prev) => {
            const updated = [...prev];
            updated[index] = imageData;
            return updated;
          });
        } else {
          setOptionalImages((prev) => [...prev, imageData]);
        }
      };
      reader.readAsDataURL(croppedFile);

      queryClient.invalidateQueries("images", { exact: true });
      return url;
    } catch (err) {
      console.error("Erro ao subir imagem cortada:", err);
    }
  };

  const handleDeleteImage = async (tag) => {
    try {
      await axios.delete(SURVEYS.DELETE_IMAGE_SURVEY, { data: { tag } });
      console.log(`Imagem com tag ${tag.name} removida com sucesso.`);
    } catch (error) {
      console.error("Erro ao remover imagem no backend:", error);
    }
  };

  const nextStep = () => {
    setActiveStep(4);
    // if (mandatoryImages?.length === mandatoryTags?.length) {
    //   setErrorMandatoryImages(false);
    //   setActiveStep(4);
    // } else {
    //   setErrorMandatoryImages(true);
    // }
  };

  return (
    <div>
      {!isLoading && !isLoadingImages ? (
        <form className="mt-5 md:mt-10 px-0 md:px-10">
          <SurveySection name="Imagens obrigatórias">
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-6 gap-2 md:gap-4">
              {mandatoryTags.map((tag, index) => (
                <PhotoUpload
                  label={index}
                  key={index}
                  onUpload={(file) => handleImageUpload(file, index, true, tag)}
                  onDelete={handleDeleteImage}
                  tag={tag}
                  images={images}
                />
              ))}
            </div>
          </SurveySection>

          <SurveySection name="Imagens opcionais">
            <div className="mb-3 flex flex-col md:flex-row gap-2">
              <AutoCompleteInput
                options={listOptionalTags}
                label={"Busca rápida por tag"}
                value={searchOptionalTag?.value}
                onChange={(tag) => setSearchOptionalTag(tag)}
              />
              {searchOptionalTag && (
                <Button
                  className="whitespace-nowrap pt-1 bg-gray-500"
                  onClick={() => setSearchOptionalTag(null)}
                >
                  Remover filtro
                </Button>
              )}
            </div>
            <div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3 mb-5 overflow-y-auto bg-gray-100 rounded-md p-2 md:p-5"
              style={{ maxHeight: "500px" }}
            >
              {optionalTags
                ?.filter(
                  (tag) =>
                    !searchOptionalTag?.value ||
                    tag.id === searchOptionalTag?.value
                )
                ?.map((tag, index) => (
                  <PhotoUpload
                    key={index}
                    onUpload={(file) =>
                      handleImageUpload(file, tag.id, false, tag)
                    }
                    onDelete={handleDeleteImage}
                    images={images}
                    tag={tag}
                    searchOptionalTag={searchOptionalTag}
                  />
                ))}
            </div>
          </SurveySection>

          <div className="flex justify-center items-center gap-2 mt-10">
            <Button onClick={() => setActiveStep(2)} className="bg-gray-500">
              Voltar
            </Button>
            <Button onClick={nextStep} className="primary">
              Próximo
            </Button>
          </div>

          {errorMandatoryImages && (
            <Alert className="mt-4" withBorderAccent color="failure">
              Você precisa subir todas as imagens obrigatórias!
            </Alert>
          )}
        </form>
      ) : (
        <div className="relative" style={{ height: "500px" }}>
          <DrawerLoading label="Carregando imagens..." />
        </div>
      )}

      {cropModalImage && (
        <CropModal
          image={cropModalImage}
          onCropComplete={async (croppedFile) => {
            setCropModalImage(null);
            await handleCroppedImageUpload(croppedFile, pendingUpload);
          }}
          onCancel={() => setCropModalImage(null)}
        />
      )}
    </div>
  );
};

export default SurveyPhoto;
